import { useCallback, useState } from 'react';
import { AlertDialog, PageHeader, Stack, TabBar, type TabBarItem } from '@leanin/ui';
import { useTranslations } from 'use-intl';
import type { IntakeFormKind } from '@leanin/shared/intake';
import { useCircle, useNetwork } from '@/lib/queries/groups';
import { IntakeFormBuilder } from './IntakeFormBuilder';

type Props = {
  readonly groupId: string;
  readonly kind: 'circle' | 'network';
};

// The "Join questions" admin section. A Circle configures its single
// `member_to_circle` form; a Network has two independent forms and switches
// between them with the platform-canonical TabBar pill strip (the same control
// as the join-requests review tabs). Switching while the current form has
// unsaved edits prompts a discard confirmation — remounting the builder on the
// active form key is what actually discards.
export function JoinQuestionsSection({ groupId, kind }: Props) {
  const t = useTranslations('Group');
  const initial: IntakeFormKind =
    kind === 'circle' ? 'member_to_circle' : 'member_to_network';

  // The group name feeds the preview's "Request to join {name}" header. Both
  // hooks share the detail cache the admin shell already populated, so this is
  // a cache read, not an extra fetch.
  const circle = useCircle(groupId, { enabled: kind === 'circle' });
  const network = useNetwork(groupId, { enabled: kind === 'network' });
  const circleObj =
    circle.data?.access === 'visitor' ? null : (circle.data?.circle ?? null);
  const groupName =
    (kind === 'circle' ? circleObj?.name : network.data?.name) ?? '';

  const [active, setActive] = useState<IntakeFormKind>(initial);
  const [dirty, setDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<IntakeFormKind | null>(null);

  const onDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  const requestSwitch = (target: IntakeFormKind) => {
    if (target === active) return;
    if (dirty) {
      setPendingSwitch(target);
      return;
    }
    setActive(target);
  };

  const confirmSwitch = () => {
    if (pendingSwitch === null) return;
    setActive(pendingSwitch);
    setPendingSwitch(null);
    setDirty(false);
  };

  const tabs: TabBarItem[] = [
    { id: 'member_to_network', label: t('admin.joinQuestions.switcher.members') },
    { id: 'circle_to_network', label: t('admin.joinQuestions.switcher.circles') },
  ];

  return (
    <Stack gap="lg">
      <PageHeader title={t('admin.joinQuestions.title')} />

      {kind === 'network' && (
        <TabBar
          ariaLabel={t('admin.joinQuestions.switcher.ariaLabel')}
          items={tabs}
          value={active}
          onChange={(id) => requestSwitch(id as IntakeFormKind)}
          overflow="scroll"
        />
      )}

      <IntakeFormBuilder
        key={active}
        groupId={groupId}
        formKind={active}
        groupName={groupName}
        onDirtyChange={onDirtyChange}
      />

      <AlertDialog
        open={pendingSwitch !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSwitch(null);
        }}
        title={t('admin.joinQuestions.discard.title')}
        description={t('admin.joinQuestions.discard.body')}
        actionLabel={t('admin.joinQuestions.discard.confirm')}
        cancelLabel={t('admin.joinQuestions.discard.cancel')}
        destructive
        onAction={confirmSwitch}
      />
    </Stack>
  );
}
